/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// UI lib components
import { ActionIcon, Box, Center, Select, Stack } from '@mantine/core';

// UI local components
import TextInput from '@ui-kit/molecules/TextInput';

// Context
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// API & Types
import { useListGroupsQuery } from '@app-types/blackroom-api';

// Style
import {
  dialogGroupInformation,
  dialogGroupInformationUsers,
} from './styles.css';
import GroupColorInput from '../../GroupColorInput';
import { Group } from '@mantine/core';
import UserPicker from '@app/features/User/UserPicker';
import { GroupWizardInformationProps } from './types';
import { IconTrash, IconUserPlus } from '@tabler/icons-react';
import UserTile from '@app/shared/ui-kit/atoms/UserTile';
import { palette } from '@app/shared/ui-kit/colors';
import GroupCheckbox from '../../GroupCheckbox';
import useGroupDisplayName from '../../hooks/useGroupDisplayName';
import { useWorkspaceUsers } from '@app/features/User/hooks/useWorkspaceUsers';
import { QueriedUser } from '@app/features/User/types';

const Field = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <>
    <Group align="center">{label}</Group>
    <Group align="center">{value}</Group>
  </>
);

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export default function GroupWizardInformation({
  createGroupInput,
  userEmails,
  inheritedGroup,
  folderId,
  setCreateGroupInput,
  setUserEmails,
  setInheritedGroup,
  setFolderId,
}: GroupWizardInformationProps) {
  /* ********************************** HOOKS ********************************* */
  const { t } = useTranslation(['Common', 'Group']);

  const workspace = useWorkspace();
  const { getDisplayName, foldersMap } = useGroupDisplayName();

  const { data: workspaceUsers } = useWorkspaceUsers();

  const availableUsers = useMemo(
    () =>
      workspaceUsers.filter(
        (workspaceUser) => !userEmails.includes(workspaceUser.email),
      ),
    [workspaceUsers, userEmails],
  );

  const folderOptions = useMemo(() => {
    const folders = Array.from(foldersMap.values());

    function buildLabel(fId: string, visited = new Set<string>()): string {
      if (visited.has(fId)) return '';
      visited.add(fId);
      const folder = foldersMap.get(fId);
      if (!folder) return '';
      if (folder.parentId) {
        const parentLabel = buildLabel(folder.parentId, visited);
        return parentLabel ? `${parentLabel}/${folder.name}` : folder.name;
      }
      return folder.name;
    }

    return folders.map((f) => ({ value: f.id, label: buildLabel(f.id) }));
  }, [foldersMap]);

  const { data: groupData } = useListGroupsQuery({
    variables: { filter: { workspaceId: workspace.id } },
    fetchPolicy: 'cache-and-network',
  });
  const groups = groupData?.groups.nodes ?? [];

  /* ********************************** HELPERS ******************************** */
  const findUserByEmail = useCallback(
    (email: string): Partial<QueriedUser> =>
      workspaceUsers.find((u) => u.email === email) ?? { email },
    [workspaceUsers],
  );

  const addUserEmail = (email: string) => {
    if (userEmails.includes(email)) return;
    setUserEmails([...userEmails, email]);
  };

  /* ******************************** RENDERING ******************************* */
  return (
    <Box className={dialogGroupInformation}>
      <Field
        key="name"
        label={t('Group:DialogGroupWizard.steps.information.fields.name.label')}
        value={
          <TextInput
            w="100%"
            defaultValue={createGroupInput.name}
            onChange={(event) =>
              setCreateGroupInput({
                ...createGroupInput,
                name: event.currentTarget.value,
              })
            }
          />
        }
      />

      {groups.length > 0 && (
        <Field
          key="inheritFromGroup"
          label={t(
            'Group:DialogGroupWizard.steps.information.fields.groupWithAccessToClone.label',
          )}
          value={
            <Select
              data={groups.map((g) => ({
                value: g.id,
                label: getDisplayName(g),
              }))}
              clearable
              w="100%"
              value={inheritedGroup}
              onChange={(value) => setInheritedGroup(value)}
              renderOption={({ option, checked }) => (
                <GroupCheckbox
                  active={checked ?? false}
                  group={groups.find((g) => g.id === option.value)!}
                  labelProps={{ hoverable: false, w: '100%' }}
                />
              )}
            />
          }
        />
      )}

      <Field
        key="color"
        label={t(
          'Group:DialogGroupWizard.steps.information.fields.color.label',
        )}
        value={
          <Group align="center" justify="end" w="100%">
            <GroupColorInput
              color={createGroupInput.color}
              onChange={(color) =>
                setCreateGroupInput({ ...createGroupInput, color })
              }
            />
          </Group>
        }
      />

      {folderOptions.length > 0 && (
        <Field
          key="folder"
          label={t(
            'Group:DialogGroupWizard.steps.information.fields.folder.label',
          )}
          value={
            <Select
              data={folderOptions}
              clearable
              w="100%"
              value={folderId}
              onChange={(value) => setFolderId(value)}
            />
          }
        />
      )}

      <Field
        key="addUser"
        label={t('Common:users.users')}
        value={
          <UserPicker
            w="100%"
            placeholder={t(
              'Group:DialogGroupWizard.steps.users.fields.userEmails.label',
            )}
            availableUsers={availableUsers}
            onPickUser={addUserEmail}
            allowCustomEmail
            leftSection={<IconUserPlus size={16} />}
          />
        }
      />

      <Stack key="users" className={dialogGroupInformationUsers} gap={10}>
        {userEmails.length === 0 ? (
          <Center h="100%" c={palette.blackroomGray}>
            {t('Group:DialogGroupWizard.steps.users.noUserInGroup')}
          </Center>
        ) : (
          userEmails.map((email) => (
            <Group
              key={email}
              align="center"
              justify="space-between"
              wrap="nowrap"
            >
              <UserTile
                user={findUserByEmail(email)}
                avatarProps={{ size: 'sm' }}
                labelProps={{ fz: 13 }}
              />
              <ActionIcon
                variant="subtle"
                color="blackroomGrey"
                onClick={() =>
                  setUserEmails(
                    userEmails.filter((userEmail) => userEmail !== email),
                  )
                }
              >
                <IconTrash size={15} color={palette.blackroomLightGray2} />
              </ActionIcon>
            </Group>
          ))
        )}
      </Stack>
    </Box>
  );
}
